/**
 * Netted per-symbol position math — Phase 6a-8 commit 20 of the decomposition
 * (briefs/ARCHITECTURE_DECOMPOSITION_BLUEPRINT.md §6, v2.19). Bodies moved
 * VERBATIM from static/paper.html; the page aliases them at the original
 * source positions.
 *
 * AUTHORITATIVE PRODUCER of these records: engine/paper_trading.py — every
 * fill (entries, resting matches, closes) happens server-side and writes the
 * same buyQty/buyValue/sellQty/sellValue/openAvg fields this math reads.
 * Keep the two in sync: this module mirrors the server engine's netting
 * semantics the way charges.ts mirrors engine/paper_risk.py.
 *
 * Record semantics (moved with the math from the page):
 * One row per (symbol, product, day) — every fill for that instrument today
 * accumulates into it, however many times it's gone flat and reopened; a new
 * calendar day starts a fresh row. Two separate things are tracked per row,
 * deliberately kept apart:
 *   1. buyQty/buyValue, sellQty/sellValue — DAY-CUMULATIVE totals,
 *      monotonically increasing, never decremented. Buying 65 qty three times
 *      today shows Buy Qty 195 at the weighted-average price across all three
 *      — this is what Nikhil asked for and matches how a real broker's
 *      Positions book displays Buy/Sell Qty/Price. (buyQty − sellQty is
 *      always the true net open qty as a matter of arithmetic, regardless of
 *      how many times the position flipped sides during the day.)
 *   2. openAvg — the cost basis of whatever's CURRENTLY open, used only to
 *      compute realized P&L on each closing leg and unrealized P&L on the
 *      remainder. This can't be derived from the cumulative buy/sell averages
 *      once there's been more than one round trip (those blend every round
 *      trip's price together), so it's tracked on its own: unchanged on a
 *      partial/full close, weighted-averaged in on a same-side add, reset to
 *      the fill price when a close fully flattens the side.
 *
 * Pure module: explicit inputs only — no state, DOM, timers, or clock.
 */

/** The subset of a position row this math reads (server blob shape). */
export interface PositionNetting {
  buyQty: number;
  buyValue: number;
  sellQty: number;
  sellValue: number;
  openAvg: number;
}

/** One open exposure leg for P&L: direction, cost basis, absolute qty. */
export interface PnlLeg {
  side: "BUY" | "SELL";
  avg: number;
  qty: number;
}

export function computePnl(pos: PnlLeg, ltp: number | null | undefined): number {
  if (ltp == null || isNaN(ltp)) return 0;
  if (pos.side === "BUY") return (ltp - pos.avg) * pos.qty;
  return (pos.avg - ltp) * pos.qty;
}

export function posNetQty(p: PositionNetting): number { return p.buyQty - p.sellQty; }
export function posSide(p: PositionNetting): "BUY" | "SELL" | "FLAT" { const n = posNetQty(p); return n > 0 ? "BUY" : n < 0 ? "SELL" : "FLAT"; }
export function posBuyAvg(p: PositionNetting): number | null { return p.buyQty > 0 ? p.buyValue / p.buyQty : null; }   // day-cumulative avg buy price (display)
export function posSellAvg(p: PositionNetting): number | null { return p.sellQty > 0 ? p.sellValue / p.sellQty : null; } // day-cumulative avg sell price (display)
export function posOpenAvg(p: PositionNetting): number { return p.openAvg; } // cost basis of currently-open exposure (P&L calc only)
export function posIsOpen(p: PositionNetting): boolean { return posNetQty(p) !== 0; }
export function posUnrealizedPnl(p: PositionNetting, ltp: number | null | undefined): number {
  if (!posIsOpen(p) || ltp == null || isNaN(ltp)) return 0;
  const net = posNetQty(p);
  return computePnl({ side: net > 0 ? "BUY" : "SELL", avg: p.openAvg, qty: Math.abs(net) }, ltp);
}

export function posPnlPct(
  p: PositionNetting,
  ltp: number | null | undefined,
  realizedGross: number = 0,
): number | null {
  const net = posNetQty(p);
  const isOpen = posIsOpen(p);
  const unrealized = posUnrealizedPnl(p, ltp);
  const total = unrealized + (realizedGross || 0);
  const costBasis = isOpen
    ? (p.openAvg > 0 ? p.openAvg * Math.abs(net) : 0)
    : ((p.buyValue > 0 ? p.buyValue : p.sellValue) || (p.openAvg > 0 ? p.openAvg * (p.buyQty || p.sellQty || 1) : 0));
  if (costBasis <= 0 || isNaN(costBasis)) return null;
  return (total / costBasis) * 100;
}

